'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { CtaButton } from '@repo/ui/CtaButton';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { theme } from '../NerdWalletThemeProvider';
import { SlideUp } from './SlideUp';
import type { CPCButtonAnalytics, CPCOfferProps } from './types';

type WealthFrontDialogProps = CPCOfferProps & {
  buttonAnalytics: CPCButtonAnalytics;
  open: boolean;
  onClose: () => void;
};

const WEALTHFRONT_CHART_MAX_HEIGHT_PX = 480;
const WEALTHFRONT_CHART_IMAGE_WIDTH_PX = 300;
const WEALTHFRONT_CHART_TRANSLATE_Y_RATIO = 0.1;
const WEALTHFRONT_CHART_SHIFT_ALIGNMENT_PX = 93;
const DIALOG_CONTENT_PADDING_X = { xs: '1.5rem', md: '2rem' } as const;

const STEP_LIST_GRID_SX: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '16px 1fr',
  columnGap: 1.5,
  rowGap: 0.5,
};

const OVERLAY_STAT_TITLE_SX: SxProps<Theme> = {
  color: 'greens.main',
  lineHeight: 1.2,
  fontWeight: 600,
};

const OVERLAY_STAT_BODY_SX: SxProps<Theme> = {
  color: 'neutral.darkest',
  lineHeight: 1.4,
  fontSize: '12px',
};

const dialogPaperMobileSx: SxProps<Theme> = {
  m: 0,
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  maxWidth: '100%',
  height: 'auto',
  maxHeight: '93vh',
  '@supports (height: 100dvh)': {
    maxHeight: '93dvh',
  },
  borderRadius: '1rem 1rem 0 0',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '2rem',
  gap: '1.25rem',
  overflowX: 'hidden',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
};

const dialogMainContentSx: SxProps<Theme> = {
  position: 'relative',
  zIndex: 1,
  paddingX: DIALOG_CONTENT_PADDING_X,
  paddingBottom: {
    xs: 'max(1.25rem, env(safe-area-inset-bottom, 0px))',
    md: '2rem',
  },
  // Mobile: don't flex-grow the sheet taller than content. Desktop: default grow for scroll within maxHeight.
  flex: { xs: '0 1 auto', md: '1 1 auto' },
  minHeight: { md: 0 },
};

const dialogPaperDesktopSx: SxProps<Theme> = {
  boxShadow: '0 8px 16px 0 rgba(100, 102, 106, 0.33), 0 4px 8px 0 rgba(100, 104, 106, 0.15)',
  width: '100%',
  maxHeight: '94vh',
  maxWidth: 1052,
  '@supports (height: 100dvh)': { maxHeight: '94dvh' },
  borderRadius: '1rem',
  paddingTop: '2rem',
  gap: '2rem',
  overflow: 'hidden',
  position: 'relative',
};

export function WealthFrontDialog({
  buttonAnalytics,
  cta,
  analyticsPayload,
  displayName,
  image,
  open,
  onClose,
}: WealthFrontDialogProps) {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const TransitionComponent = useMemo(() => (isMobile ? SlideUp : Fade), [isMobile]);
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setContentReady(false);
      return;
    }
    let id1: number | undefined;
    const id2 = requestAnimationFrame(() => {
      // Defer one frame past paint so layout-dependent children (logo, chart) measure correctly.
      id1 = requestAnimationFrame(() => {
        setContentReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(id2);
      if (id1 != null) cancelAnimationFrame(id1);
    };
  }, [open]);

  if (!analyticsPayload.offerId || !cta) {
    return null;
  }

  return (
    <Dialog
      fullWidth={!isMobile}
      maxWidth={false}
      keepMounted
      onClose={onClose}
      open={open}
      slots={{ transition: TransitionComponent }}
      slotProps={{
        paper: {
          sx: isMobile ? dialogPaperMobileSx : dialogPaperDesktopSx,
        },
      }}
      sx={
        isMobile
          ? {
              '& .MuiDialog-container': {
                alignItems: 'flex-end',
              },
            }
          : undefined
      }
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: DIALOG_CONTENT_PADDING_X,
            py: '10px',
            height: '20px',
            maxHeight: '20px',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minWidth: 0,
              height: '20px',
              maxHeight: '20px',
            }}
          >
            {contentReady && image?.source ? (
              <Image
                src={image.source}
                alt={image.alt || displayName || ''}
                fill
                style={{
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  maxHeight: '25px',
                }}
                unoptimized
              />
            ) : null}
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              flexShrink: 0,
              color: 'neutral.darkest',
              fontSize: {
                md: '1.5rem',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {contentReady ? (
        <>
          <DialogContent dividers={false} sx={dialogMainContentSx}>
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                maxWidth: { xs: '100%', md: '50%' },
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '8px', sm: 0 },
                }}
              >
                <Typography
                  variant="text"
                  sx={{
                    color: 'greens.main',
                    fontSize: { xs: '24px', sm: '36px' },
                    lineHeight: '125%',
                    letterSpacing: '0.18px',
                    fontWeight: 600,
                  }}
                >
                  Earn more with two great rewards
                </Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  Boost the 3.30% base variable APY to 4.30% for a limited time in two steps:
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: { xs: 3, sm: 4 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, sm: 4 } }}>
                  <Box sx={STEP_LIST_GRID_SX}>
                    <Typography variant="textBold" sx={{ fontWeight: 600 }}>
                      1.
                    </Typography>
                    <Typography variant="textBold" sx={{ fontWeight: 600 }}>
                      Open and fund a new Cash Account
                    </Typography>

                    <Box />
                    <Typography variant="text" sx={{ fontSize: '14px' }}>
                      Fund your new account within 30 days to earn a 0.75% APY boost for 3 months on
                      up to a $150,000 balance.
                    </Typography>
                  </Box>

                  <Box sx={STEP_LIST_GRID_SX}>
                    <Typography variant="textBold" sx={{ fontWeight: 600 }}>
                      2.
                    </Typography>
                    <Typography variant="textBold" sx={{ fontWeight: 600 }}>
                      Direct deposit and invest with Wealthfront
                    </Typography>

                    <Box />
                    <Typography variant="text" sx={{ fontSize: '14px' }}>
                      Get a 0.25% APY increase - no expiration date or balance limit - when you
                      direct deposit $1,000 a month into your Cash Account, plus open, fund, and
                      maintain any investing account.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  width: '100%',
                  mt: { xs: '24px', md: '32px' },
                }}
              >
                <Box sx={{ width: { xs: '100%', sm: '342px' } }}>
                  <CtaButton
                    cta={cta}
                    analyticsPayload={analyticsPayload}
                    impressionSectionOverride={buttonAnalytics.impressionSection}
                    fullWidth
                    subtextStylesOverride={{
                      display: 'block',
                      marginX: 0,
                      fontSize: '11px',
                      lineHeight: '17px',
                      paddingTop: '4px',
                    }}
                    buttonStylesOverride={{
                      fontSize: '14px',
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                mt: { xs: '16px', md: '24px' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <Typography
                variant="text"
                sx={{ fontSize: { xs: '11px', sm: '12px' }, color: 'neutral.darkest' }}
              >
                Terms apply. Full details at{' '}
                <Link
                  href="https://www.wealthfront.com/promotional-terms"
                  target="_blank"
                  rel="noopener"
                  sx={{
                    color: 'blues.main',
                    textDecoration: 'underline',
                  }}
                >
                  wealthfront.com/promo-terms.
                </Link>
              </Typography>
            </Box>
          </DialogContent>

          <Box
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              width: '50%',
              display: { xs: 'none', md: 'block' },
              boxSizing: 'border-box',
              zIndex: 0,
              paddingLeft: 2,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                ml: 0,
                mr: 'auto',
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                alignItems: 'start',
                alignContent: 'start',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'relative',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Image
                  width={WEALTHFRONT_CHART_IMAGE_WIDTH_PX}
                  height={WEALTHFRONT_CHART_MAX_HEIGHT_PX}
                  src="https://www.nerdwallet.com/cdn/banking/wealthfront-modal/wealthfront-graph.svg"
                  alt=""
                  unoptimized
                  style={{
                    display: 'block',
                    width: `${WEALTHFRONT_CHART_IMAGE_WIDTH_PX}px`,
                    height: 'auto',
                    maxHeight: WEALTHFRONT_CHART_MAX_HEIGHT_PX,
                    objectFit: 'contain',
                    transform: `translateY(${WEALTHFRONT_CHART_TRANSLATE_Y_RATIO * 100}%)`,
                  }}
                />
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  width: '100%',
                  pointerEvents: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignSelf: 'start',
                  justifySelf: 'stretch',
                  paddingTop: `${WEALTHFRONT_CHART_SHIFT_ALIGNMENT_PX}px`,
                  paddingRight: 3,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="textLargeBold" sx={OVERLAY_STAT_TITLE_SX}>
                    +0.75%
                  </Typography>
                  <Typography variant="text" sx={OVERLAY_STAT_BODY_SX}>
                    3 month boost for new clients on up to a $150,000
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="textLargeBold" sx={OVERLAY_STAT_TITLE_SX}>
                    +0.25%
                  </Typography>
                  <Typography variant="text" sx={OVERLAY_STAT_BODY_SX}>
                    increase for clients who direct deposit and invest
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: '15px' }}>
                  <Typography variant="textLargeBold" sx={OVERLAY_STAT_TITLE_SX}>
                    3.30%
                  </Typography>
                  <Typography variant="text" sx={OVERLAY_STAT_BODY_SX}>
                    Base APY
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <DialogContent dividers={false} sx={dialogMainContentSx} />
      )}
    </Dialog>
  );
}
